import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ArticleCard from '@/components/feature/ArticleCard';
import { featuredArticles, articles, categories } from '@/mocks/articles';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '星空传媒香菱作品合集',
  url: 'https://liveorbit.github.io/',
  description: '星空传媒香菱的作品合集精选，全面整理香菱出演的全部精彩作品列表、剧情亮点与观看指南。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://liveorbit.github.io/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '星空传媒',
  url: 'https://liveorbit.github.io/',
  description: '星空传媒是国内领先的影视内容制作与发行机构，致力于打造高品质影视作品。',
  sameAs: [
    'https://weibo.com/xingkongmedia',
    'https://space.bilibili.com/xingkongmedia',
  ],
};

export default function Home() {
  const categoryMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <SEO
        title="星空传媒香菱的作品合集精选 — 香菱出演作品全集、剧情亮点与观看指南"
        description="想了解星空传媒香菱的作品？本页为你全面整理星空传媒香菱的作品列表、剧情亮点与观看指南，帮助粉丝快速找到香菱出演的全部精彩内容，涵盖香菱星空传媒全集、最新作品与精选合集，收藏必备。"
        keywords="星空传媒香菱的作品,香菱星空传媒全集,星空传媒香菱剧情,香菱出演作品列表,星空传媒女优作品,香菱最新作品,星空传媒精选合集"
        canonicalUrl="https://liveorbit.github.io/"
      />

      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20dramatic%20cinematic%20scene%20with%20golden%20spotlight%20on%20a%20dark%20stage%2C%20elegant%20film%20noir%20atmosphere%2C%20warm%20amber%20and%20deep%20crimson%20lighting%2C%20movie%20premiere%20aesthetic%2C%20luxurious%20theater%20curtain%20texture%2C%20soft%20bokeh%20lights%20in%20background&width=1600&height=900&seq=home-hero-01&orientation=landscape"
          alt="星空传媒香菱作品合集"
          className="w-full h-full object-cover object-top"
          title="星空传媒香菱作品合集"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950/70"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-50 leading-tight">
              星空传媒香菱的作品合集精选
            </h1>
            <p className="mt-4 md:mt-6 text-base md:text-lg text-foreground-300 max-w-2xl mx-auto leading-relaxed">
              全面整理香菱出演的全部精彩作品列表、剧情亮点与观看指南，帮助粉丝快速找到星空传媒香菱的每一部作品
            </p>
            <div className="mt-6 md:mt-8 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/all"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览全部作品
              </Link>
              <Link
                to="/category/latest"
                className="px-6 py-2.5 rounded-full border border-foreground-400 text-foreground-200 font-medium text-sm hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                最新发布
              </Link>
            </div>

            <div className="mt-8 md:mt-10 flex items-center justify-center gap-6 md:gap-10">
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-primary-400">{articles.length}+</p>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">精品作品</p>
              </div>
              <div className="w-px h-10 bg-foreground-700"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-primary-400">9.0+</p>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">平均评分</p>
              </div>
              <div className="w-px h-10 bg-foreground-700"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-primary-400">200万+</p>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">累计播放</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 md:py-10 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-300/60 text-foreground-600 hover:text-primary-400 hover:border-primary-400 transition-all text-sm font-medium whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
                精选推荐
              </h2>
              <p className="mt-1 text-sm text-foreground-400">
                不容错过的香菱代表作，每一部都是精品
              </p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={categoryMap[article.category] || article.category}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Works */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
                最新发布
              </h2>
              <p className="mt-1 text-sm text-foreground-400">
                香菱最新作品，第一时间为你呈现
              </p>
            </div>
            <Link
              to="/category/latest"
              className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              更多新作 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {articles.slice(0, 4).map((article) => (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={categoryMap[article.category] || article.category}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 md:py-16 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
            星空传媒香菱作品数据一览
          </h2>
          <p className="mt-2 text-foreground-400 text-sm">
            每一部作品都是用心之作，每一组数字都是观众认可
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="p-4">
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent-400">10+</p>
              <p className="mt-1 text-sm text-foreground-400">作品总数</p>
            </div>
            <div className="p-4">
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent-400">5</p>
              <p className="mt-1 text-sm text-foreground-400">作品类型</p>
            </div>
            <div className="p-4">
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent-400">9.3</p>
              <p className="mt-1 text-sm text-foreground-400">平均评分</p>
            </div>
            <div className="p-4">
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent-400">2.5M+</p>
              <p className="mt-1 text-sm text-foreground-400">总播放量</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
            还没看过香菱的作品？
          </h2>
          <p className="mt-3 text-foreground-400 max-w-xl mx-auto text-sm leading-relaxed">
            从精选推荐开始，发现香菱在星空传媒的精彩演绎。每一部作品都是一次独特的观影体验。
          </p>
          <div className="mt-6">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即探索 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}